(() => {
  const ROOT_ID = "agoz-cart-drawer-root";

  // Endpoints
  const LEAD_ENDPOINT =
    window.AGOZ_CART_LEAD_ENDPOINT ||
    "https://snazzy-travesseiro-3fde86.netlify.app/.netlify/functions/cart-lead-public";

  const EMAIL_ENDPOINT =
    window.AGOZ_CART_EMAIL_ENDPOINT ||
    "https://snazzy-travesseiro-3fde86.netlify.app/.netlify/functions/cart-email";

  // localStorage keys
  const LS_EMAIL = "agoz_cart_gate_email";
  const LS_SENT_TOKEN = "agoz_cart_gate_sent_for_token";

  // Cart # keys
  const CART_ATTR_KEY = "agoz_cart_number"; // Shopify cart attribute key
  const LS_CART_NUM = "agoz_cart_number";   // local fallback

  const SELECTORS_TO_OPEN = [
    'a[href="/cart"]',
    'a[href="/cart/"]',
    'a[href*="/cart"]',
    "[data-header-cart]",
    "[data-cart-toggle]",
    '.header-actions a[href="/cart"]'
  ];

  const state = {
    view: "cart", // "cart" | "email"
    cart: null,
    cartNumber: "",
    ensuringCartNumber: false,

    libsPreloaded: false,
    addToCartPatched: false,

    gateAutoFocus: false,

    // gate
    gate: {
      unlocked: false,
      sending: false,
      error: "",
      email: "",
      leadId: ""
    },

    // email cart view
    email: {
      sending: false,
      error: "",
      success: ""
    }
  };

  // --------------------------
  // Helpers
  // --------------------------
  const money = (cents) => {
    const n = (cents || 0) / 100;

    const currency =
      (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) ||
      "USD";

    try {
      return new Intl.NumberFormat(undefined, { style: "currency", currency }).format(n);
    } catch {
      return `${currency} ${n.toFixed(2)}`;
    }
  };

  const esc = (s) =>
    String(s ?? "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#039;");

  const getRoot = () => document.getElementById(ROOT_ID);

  const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(email || "").trim());

  function buildCartPermalink(cart) {
    try {
      const items = (cart?.items || [])
        .filter((it) => Number(it?.quantity || 0) > 0 && it?.id)
        .map((it) => `${it.id}:${Number(it.quantity)}`);

      if (!items.length) return `${location.origin}/cart`;
      return `${location.origin}/cart/${items.join(",")}`;
    } catch {
      return `${location.origin}/cart`;
    }
  }

  // --------------------------
  // Cart # (6 digits)
  // --------------------------
  function genCartNumber6() {
    const n = Math.floor(100000 + Math.random() * 900000);
    return String(n);
  }

  function getCartNumberFromCart(cart) {
    const v = cart?.attributes?.[CART_ATTR_KEY];
    return v ? String(v).trim() : "";
  }

  function getCartNumberFromLocal() {
    return (localStorage.getItem(LS_CART_NUM) || "").trim();
  }

  function setCartNumberLocal(v) {
    try { localStorage.setItem(LS_CART_NUM, String(v || "")); } catch (_) {}
  }

  async function setCartNumberAttribute(cartNumber) {
    const payload = { attributes: {} };
    payload.attributes[CART_ATTR_KEY] = String(cartNumber);

    const res = await fetch("/cart/update.js", {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload)
    });

    if (!res.ok) throw new Error("cart/update.js failed");
    return await res.json();
  }

  async function ensureCartNumber(cart) {
    if (state.ensuringCartNumber) return;

    let n = getCartNumberFromCart(cart);
    if (!n) n = getCartNumberFromLocal();
    if (!n) n = genCartNumber6();

    state.cartNumber = n;
    setCartNumberLocal(n);

    if (getCartNumberFromCart(cart) === n) return;

    state.ensuringCartNumber = true;
    try {
      const updated = await setCartNumberAttribute(n);
      state.cart = updated;
      state.cartNumber = getCartNumberFromCart(updated) || n;
      setCartNumberLocal(state.cartNumber);
    } catch (e) {
      console.warn("[AGOZ_CART_DRAWER] ensureCartNumber failed:", e);
    } finally {
      state.ensuringCartNumber = false;
    }
  }

  // --------------------------
  // Gate inline styles
  // --------------------------
  function injectGateStylesOnce() {
    if (document.getElementById("agoz-cart-gate-styles")) return;

    const style = document.createElement("style");
    style.id = "agoz-cart-gate-styles";
    style.textContent = `
      body.agoz-cart-open.agoz-cart-gated .agoz-cart-body,
      body.agoz-cart-open.agoz-cart-gated .agoz-cart-footer{
        filter: blur(7px);
        pointer-events:none;
        user-select:none;
      }

      .agoz-gate-overlay{
        position: absolute;
        inset: 64px 0 0 0;
        display:flex;
        align-items:center;
        justify-content:center;
        padding: 18px 14px;
        z-index: 5;
      }

      .agoz-gate-card{
        width: 100%;
        max-width: 520px;
        background:#fff;
        border:1px solid #E5E7EB;
        border-radius: 18px;
        box-shadow: 0 18px 45px rgba(0,0,0,0.12);
        overflow:hidden;
      }

      .agoz-gate-card-inner{
        padding: 16px 16px 14px 16px;
      }

      .agoz-gate-title{
        font-weight: 900;
        font-size: 18px;
        color:#111827;
        line-height: 1.2;
        margin: 0 0 8px 0;
      }

      .agoz-gate-sub{
        font-weight: 600;
        font-size: 13px;
        color:#6B7280;
        line-height: 1.35;
        margin: 0 0 14px 0;
      }

      .agoz-gate-label{
        display:block;
        font-weight: 900;
        font-size: 13px;
        color:#111827;
        margin-bottom: 8px;
      }

      .agoz-gate-input{
        width:100%;
        height: 46px;
        border-radius: 12px;
        border: 2px solid #E5E7EB;
        outline:none;
        padding: 0 12px;
        font-size: 14px;
        font-weight: 700;
        color:#111827;
        background:#fff;
        box-sizing:border-box;
      }

      .agoz-gate-error{
        margin-top: 10px;
        font-size: 13px;
        font-weight: 800;
        color: #b91c1c;
      }

      .agoz-gate-btn{
        margin-top: 16px;
        width:100%;
        height: 52px;
        border-radius: 14px;
        border:none;
        background: #e5e257;
        color:#111827;
        font-weight: 900;
        font-size: 15px;
        cursor:pointer;
      }
      .agoz-gate-btn[disabled]{
        opacity:.6;
        cursor:not-allowed;
      }

      .agoz-gate-success{
        margin-top: 10px;
        font-size: 13px;
        font-weight: 900;
        color:#065f46;
      }

      .agoz-gate-open-btn{
        margin-top: 16px;
        width:100%;
        height: 52px;
        border-radius: 14px;
        border:none;
        background: #e5e257;
        color:#111827;
        font-weight: 900;
        font-size: 15px;
        cursor:pointer;
      }

      @keyframes agoz-spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }

      @keyframes agoz-progress {
        0% { width: 0%; }
        40% { width: 60%; }
        80% { width: 85%; }
        100% { width: 95%; }
      }

      .agoz-btn-loading {
        position: relative !important;
        overflow: hidden !important;
        display: inline-flex !important;
        align-items: center !important;
        justify-content: center !important;
        gap: 8px !important;
        opacity: 0.85 !important;
        cursor: wait !important;
      }

      .agoz-spinner {
        width: 14px;
        height: 14px;
        border: 2px solid rgba(17, 24, 39, 0.25);
        border-top-color: #111827;
        border-radius: 50%;
        animation: agoz-spin 0.8s linear infinite;
        display: inline-block;
        flex-shrink: 0;
      }

      .agoz-loading-bar {
        position: absolute;
        bottom: 0;
        left: 0;
        height: 4px;
        background: #111827;
        animation: agoz-progress 6s cubic-bezier(0.1, 0.5, 0.5, 1) forwards;
      }
    `;
    document.head.appendChild(style);
  }

  function loadGateStateFromLocalStorage() {
    const email = (localStorage.getItem(LS_EMAIL) || "").trim().toLowerCase();
    const sentToken = (localStorage.getItem(LS_SENT_TOKEN) || "").trim();

    state.gate.email = email;
    state.gate.unlocked = !!email && !!sentToken;
  }

  function setGatedBodyClass() {
    if (!document.body.classList.contains("agoz-cart-open")) return;

    if (state.view !== "cart") {
      document.body.classList.remove("agoz-cart-gated");
      return;
    }

    if (state.gate.unlocked) document.body.classList.remove("agoz-cart-gated");
    else document.body.classList.add("agoz-cart-gated");
  }

  // --------------------------
  // Auto-open after Add to Cart
  // --------------------------
  function isCartAddUrl(url) {
    const u = String(url || "");
    return /\/cart\/add(\.js)?(\?|$)/i.test(u);
  }

  function onAddToCartSuccess() {
    if ((location.pathname || "").startsWith("/cart")) return;

    if (document.body.classList.contains("agoz-cart-open")) {
      refresh();
      return;
    }

    open();
  }

  function patchAddToCartAutoOpen() {
    if (state.addToCartPatched) return;
    state.addToCartPatched = true;

    if (typeof window.fetch === "function") {
      const originalFetch = window.fetch.bind(window);
      window.fetch = async (...args) => {
        const input = args[0];
        const init = args[1] || {};

        const url =
          typeof input === "string"
            ? input
            : input && typeof input.url === "string"
              ? input.url
              : "";

        const method = String((init && init.method) || (input && input.method) || "GET").toUpperCase();
        const isAdd = isCartAddUrl(url) && method === "POST";

        const res = await originalFetch(...args);

        if (isAdd && res && res.ok) {
          setTimeout(onAddToCartSuccess, 120);
        }

        return res;
      };
    }

    if (window.XMLHttpRequest && XMLHttpRequest.prototype) {
      const origOpen = XMLHttpRequest.prototype.open;
      const origSend = XMLHttpRequest.prototype.send;

      XMLHttpRequest.prototype.open = function (method, url, ...rest) {
        this.__agoz_method = method;
        this.__agoz_url = url;
        return origOpen.call(this, method, url, ...rest);
      };

      XMLHttpRequest.prototype.send = function (body) {
        try {
          const url = this.__agoz_url || "";
          const method = String(this.__agoz_method || "GET").toUpperCase();
          const isAdd = isCartAddUrl(url) && method === "POST";

          if (isAdd) {
            this.addEventListener(
              "load",
              () => {
                if (this.status >= 200 && this.status < 300) {
                  setTimeout(onAddToCartSuccess, 120);
                }
              },
              { once: true }
            );
          }
        } catch (_) {}

        return origSend.call(this, body);
      };
    }
  }

  // --------------------------
  // Mount / UI
  // --------------------------
  function mount() {
    const root = getRoot();
    if (!root) return;

    injectGateStylesOnce();
    loadGateStateFromLocalStorage();

    root.innerHTML = `
      <div class="agoz-cart-overlay" data-agoz-overlay></div>
      <aside class="agoz-cart-drawer" role="dialog" aria-modal="true" aria-label="Cart Drawer" style="position:relative;">
        <header class="agoz-cart-header">
          <div class="agoz-header-left">
            <button class="agoz-icon-btn" data-agoz-back aria-label="Back" style="display:none;">
              ${iconBack()}
            </button>
          </div>

          <div class="agoz-header-center">
            <div class="agoz-cart-title" data-agoz-title>My Cart</div>
          </div>

          <div class="agoz-header-right">
            <div class="agoz-icon-pill" aria-label="Cart items">
              ${iconCart()}
              <div class="agoz-cart-badge" data-agoz-badge>0</div>
            </div>
            <button class="agoz-icon-btn" data-agoz-close aria-label="Close">
              ${iconClose()}
            </button>
          </div>
        </header>

        <section class="agoz-cart-body" data-agoz-body></section>
        <footer class="agoz-cart-footer" data-agoz-footer></footer>
        <div data-agoz-gate-host></div>
      </aside>
    `;

    root.querySelector("[data-agoz-overlay]")?.addEventListener("click", close);
    root.querySelector("[data-agoz-close]")?.addEventListener("click", close);
    root.querySelector("[data-agoz-back]")?.addEventListener("click", () => {
      state.view = "cart";
      render();
    });

    document.addEventListener(
      "click",
      (e) => {
        const r = getRoot();
        if (r && e.target && r.contains(e.target)) return;

        const a = e.target?.closest?.("a,button");
        if (!a) return;

        const shouldOpen = SELECTORS_TO_OPEN.some((sel) => a.matches?.(sel) || a.closest?.(sel));
        if (!shouldOpen) return;

        const href = a.getAttribute("href") || "";
        const isCartLink = href.includes("/cart");
        if (isCartLink) e.preventDefault();

        open();
      },
      true
    );

    patchAddToCartAutoOpen();

    refresh();
    preloadPdfLibs();
  }

  async function refresh() {
    try {
      const res = await fetch("/cart.js", { credentials: "same-origin" });
      if (!res.ok) throw new Error("cart.js failed");
      state.cart = await res.json();

      await ensureCartNumber(state.cart);

      state.cartNumber = getCartNumberFromCart(state.cart) || state.cartNumber || getCartNumberFromLocal();

      render();
    } catch (e) {
      state.cart = { item_count: 0, items: [], total_price: 0, attributes: {} };
      state.cartNumber = state.cartNumber || getCartNumberFromLocal() || "";
      render();
      console.warn("[AGOZ_CART_DRAWER] refresh failed", e);
    }
  }

  function open() {
    document.body.classList.add("agoz-cart-open");
    if (state.view !== "cart") state.view = "cart";
    loadGateStateFromLocalStorage();

    if (!state.gate.unlocked) state.gateAutoFocus = true;

    refresh();
  }

  function close() {
    document.body.classList.remove("agoz-cart-open");
    document.body.classList.remove("agoz-cart-gated");
  }

  function toggle() {
    if (document.body.classList.contains("agoz-cart-open")) close();
    else open();
  }

  // --------------------------
  // Sync header cart bubble count
  // --------------------------
  const HEADER_BUBBLE_SELECTORS = [
    ".site-header-cart--count",
    "[data-header-cart-count]",
    ".cart-count-bubble",
    ".site-header__cart-count",
    ".header-cart-count",
    ".header__cart-count",
    "[data-cart-count]",
    "[data-cart-count-bubble]",
    "[data-cart-count-number]"
  ];

  function getOrCreatePrimaryBubble(btn) {
    let primary =
      btn.querySelector(".site-header-cart--count[data-header-cart-count]") ||
      btn.querySelector(".site-header-cart--count") ||
      btn.querySelector("[data-header-cart-count]");

    if (!primary) {
      primary = document.createElement("span");
      primary.className = "site-header-cart--count";
      primary.setAttribute("data-header-cart-count", "0");
      btn.prepend(primary);
    }

    const all = btn.querySelectorAll(HEADER_BUBBLE_SELECTORS.join(","));
    all.forEach((el) => {
      if (el === primary) return;
      el.style.display = "none";
    });

    return primary;
  }

  function syncHeaderCartCount(count) {
    const n = Math.max(0, Number(count || 0));

    document
      .querySelectorAll(
        ".site-header-cart--button, .site-header-cart a[href='/cart'], a.site-header-cart--button[href='/cart'], a[href='/cart'], a[href='/cart/']"
      )
      .forEach((btn) => {
        const primary = getOrCreatePrimaryBubble(btn);

        primary.textContent = String(n);
        primary.setAttribute("data-header-cart-count", String(n));

        if (primary.classList) {
          if (n > 0) primary.classList.add("visible");
          else primary.classList.remove("visible");
        }

        primary.style.display = n > 0 ? "" : "none";
        btn.setAttribute("aria-label", n > 0 ? `Cart (${n})` : "Cart");
      });

    try {
      document.dispatchEvent(new CustomEvent("agoz:cart:updated", { detail: { item_count: n } }));
    } catch (_) {}
  }

  function setBadge(n) {
    const root = getRoot();
    const badge = root?.querySelector("[data-agoz-badge]");
    const count = Math.max(0, Number(n || 0));

    if (badge) badge.textContent = String(count);
    syncHeaderCartCount(count);
  }

  // --------------------------
  // Render
  // --------------------------
  function render() {
    const root = getRoot();
    if (!root) return;

    const titleEl = root.querySelector("[data-agoz-title]");
    const backBtn = root.querySelector("[data-agoz-back]");
    const bodyEl = root.querySelector("[data-agoz-body]");
    const footerEl = root.querySelector("[data-agoz-footer]");
    const gateHost = root.querySelector("[data-agoz-gate-host]");

    const cart = state.cart || { item_count: 0, items: [], total_price: 0, attributes: {} };
    setBadge(cart.item_count || 0);

    titleEl.textContent = "My Cart";

    if (state.view === "email") backBtn.style.display = "";
    else backBtn.style.display = "none";

    if (state.view === "email") {
      bodyEl.innerHTML = renderEmailView();
      footerEl.innerHTML = "";
      gateHost.innerHTML = "";
      document.body.classList.remove("agoz-cart-gated");
      wireEmailView(bodyEl);
      return;
    }

    bodyEl.innerHTML = renderCartItems(cart);
    footerEl.innerHTML = renderFooter(cart);

    loadGateStateFromLocalStorage();
    setGatedBodyClass();

    if (!state.gate.unlocked) {
      gateHost.innerHTML = renderGate();
      wireGate(gateHost);

      if (state.gateAutoFocus) {
        state.gateAutoFocus = false;
        setTimeout(() => {
          const emailEl = gateHost.querySelector("[data-agoz-gate-email]");
          if (emailEl) {
            emailEl.focus({ preventScroll: true });
            try {
              const len = (emailEl.value || "").length;
              emailEl.setSelectionRange(len, len);
            } catch (_) {}
          }
        }, 0);
      }
    } else {
      gateHost.innerHTML = "";
    }

    wireCart(bodyEl, footerEl, cart);
  }

  // --------------------------
  // Gate
  // --------------------------
  function renderGate() {
    const error = state.gate.error ? `<div class="agoz-gate-error">${esc(state.gate.error)}</div>` : "";
    const success = state.gate.leadId
      ? `<div class="agoz-gate-success">Cart will unlock right after you add your email.</div>`
      : "";

    const showOpenBtn = !!state.gate.leadId;

    return `
      <div class="agoz-gate-overlay">
        <div class="agoz-gate-card">
          <div class="agoz-gate-card-inner">
            <div class="agoz-gate-title">Unlock your cart</div>
            <div class="agoz-gate-sub">Cart will unlock right after you add your email.</div>

            <label class="agoz-gate-label">Email *</label>
            <input class="agoz-gate-input" type="email" placeholder="you@company.com" data-agoz-gate-email value="${esc(
              state.gate.email || ""
            )}" />

            ${error}
            ${success}

            ${
              showOpenBtn
                ? `<button class="agoz-gate-open-btn" type="button" data-agoz-gate-open>See my cart</button>`
                : `<button class="agoz-gate-btn" type="button" data-agoz-gate-submit ${
                    state.gate.sending ? "disabled" : ""
                  }>
                     ${state.gate.sending ? "Saving..." : "Unlock cart"}
                   </button>`
            }
          </div>
        </div>
      </div>
    `;
  }

  async function sendLead(email, cart) {
    const controller = new AbortController();
    const t = setTimeout(() => controller.abort(), 10000);

    try {
      const cartNumber = state.cartNumber || getCartNumberFromCart(cart) || getCartNumberFromLocal() || "";

      const res = await fetch(LEAD_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email,
          cart,
          resumeCartUrl: buildCartPermalink(cart),
          cartNumber,
          cart_number: cartNumber,
          cartNo: cartNumber
        }),
        signal: controller.signal
      });

      const json = await res.json().catch(() => null);
      if (!res.ok || !json || json.ok !== true) {
        const msg = json && json.error ? json.error : `Request failed (${res.status})`;
        throw new Error(msg);
      }

      return json;
    } finally {
      clearTimeout(t);
    }
  }

  function wireGate(gateHost) {
    const emailEl = gateHost.querySelector("[data-agoz-gate-email]");
    const submitBtn = gateHost.querySelector("[data-agoz-gate-submit]");
    const openBtn = gateHost.querySelector("[data-agoz-gate-open]");

    emailEl?.addEventListener("pointerdown", (e) => e.stopPropagation(), true);
    emailEl?.addEventListener("mousedown", (e) => e.stopPropagation(), true);
    emailEl?.addEventListener("click", (e) => e.stopPropagation(), true);
    emailEl?.addEventListener("touchstart", (e) => e.stopPropagation(), { passive: true });

    emailEl?.addEventListener("input", () => {
      state.gate.email = (emailEl.value || "").trim().toLowerCase();
      state.gate.error = "";
    });

    submitBtn?.addEventListener("click", async () => {
      const email = (emailEl?.value || "").trim().toLowerCase();

      if (!email) {
        state.gate.error = "Please enter your email.";
        render();
        return;
      }
      if (!isValidEmail(email)) {
        state.gate.error = "Please enter a valid email.";
        render();
        return;
      }

      state.gate.sending = true;
      state.gate.error = "";
      render();

      try {
        const cart = state.cart || { item_count: 0, items: [], total_price: 0, attributes: {} };
        await ensureCartNumber(cart);

        const resp = await sendLead(email, state.cart || cart);

        localStorage.setItem(LS_EMAIL, email);
        localStorage.setItem(LS_SENT_TOKEN, resp.leadId || "sent");

        state.gate.leadId = resp.leadId || "";
        state.gate.unlocked = true;

        document.body.classList.remove("agoz-cart-gated");
        render();
      } catch (e) {
        state.gate.error = e?.message || "Could not save your email. Please try again.";
        state.gate.sending = false;
        render();
      } finally {
        state.gate.sending = false;
      }
    });

    openBtn?.addEventListener("click", () => {
      state.gate.unlocked = true;
      document.body.classList.remove("agoz-cart-gated");
      gateHost.innerHTML = "";
    });
  }

  // --------------------------
  // Cart UI
  // --------------------------
  function renderCartItems(cart) {
    if (!cart.items || cart.items.length === 0) {
      return `<div class="agoz-cart-empty">Your cart is empty.</div>`;
    }

    const itemsHtml = cart.items
      .map((it, idx) => {
        const img = it.image ? it.image.replace(/(\?.*)$/, "") : "";
        const unit = it.price || 0;
        const lineTotal = it.line_price != null ? it.line_price : unit * (it.quantity || 0);

        return `
          <div class="agoz-item" data-line="${idx + 1}">
            <img class="agoz-item-img" src="${esc(img)}" alt="${esc(it.product_title || it.title)}" />
            <div class="agoz-item-main">
              <div class="agoz-item-top">
                <div style="min-width:0;">
                  <p class="agoz-item-title">${esc(it.product_title || it.title || "")}</p>
                  <div class="agoz-item-each">${money(unit)} each</div>
                </div>
                <div class="agoz-item-price">${money(lineTotal)}</div>
              </div>

              <div style="display:flex; align-items:center; gap:10px;">
                <div class="agoz-qty">
                  <button type="button" data-qty-minus aria-label="Decrease quantity">−</button>
                  <input type="number" inputmode="numeric" min="0" step="1" value="${Number(
                    it.quantity || 0
                  )}" data-qty-input aria-label="Quantity" />
                  <button type="button" data-qty-plus aria-label="Increase quantity">+</button>
                </div>

                <button type="button" class="agoz-remove" data-remove>Remove</button>
              </div>
            </div>
          </div>
        `;
      })
      .join("");

    return `<div class="agoz-cart-items">${itemsHtml}</div>`;
  }

  function renderFooter(cart) {
    return `
      <div class="agoz-cart-footer-actions">
        <button class="agoz-btn" type="button" data-email-cart>Email Cart</button>
        <button class="agoz-btn" type="button" data-download-pdf>Download PDF</button>
      </div>

      <div class="agoz-subtotal-row">
        <div class="label">Subtotal</div>
        <div class="value">${money(cart.total_price || 0)}</div>
      </div>

      <button class="agoz-checkout" type="button" data-checkout>Checkout</button>
    `;
  }

  // --------------------------
  // Email Cart (Netlify function)
  // --------------------------
  function renderEmailView() {
    const err = state.email.error ? `<div class="agoz-email-error show">${esc(state.email.error)}</div>` : "";
    const ok = state.email.success ? `<div class="agoz-email-success">${esc(state.email.success)}</div>` : "";

    return `
      <div class="agoz-email-view">
        <div class="agoz-email-h1">Email Your Cart</div>

        <div class="agoz-email-grid">
          <div class="agoz-field">
            <label>Your Full Name *</label>
            <input type="text" data-email-name placeholder="John Doe" />
          </div>
          <div class="agoz-field">
            <label>Your Email *</label>
            <input type="email" data-email-to placeholder="john@company.com" />
          </div>
        </div>

        <div class="agoz-field">
          <label>Send Notes</label>
          <textarea data-email-notes placeholder=""></textarea>
        </div>

        ${err}
        ${ok}

        <div class="agoz-email-actions">
          <button class="btn-send" type="button" data-email-send ${state.email.sending ? "disabled" : ""}>
            ${state.email.sending ? "Sending..." : "Send Email"}
          </button>
          <button class="btn-cancel" type="button" data-email-cancel ${state.email.sending ? "disabled" : ""}>Cancel</button>
        </div>
      </div>
    `;
  }

  async function sendCartEmail({ to, name, notes, cart }) {
    const controller = new AbortController();
    const t = setTimeout(() => controller.abort(), 12000);

    try {
      const resumeUrl = buildCartPermalink(cart);
      const cartNumber = state.cartNumber || getCartNumberFromCart(cart) || getCartNumberFromLocal() || "";

      const payload = {
        to,
        email: to,
        toEmail: to,
        recipient: to,
        customer_email: to,

        name,
        full_name: name,

        notes,
        message: notes,

        cart,
        resumeCartUrl: resumeUrl,
        cart_url: resumeUrl,

        cartNumber,
        cart_number: cartNumber,
        cartNo: cartNumber
      };

      const res = await fetch(EMAIL_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
        signal: controller.signal
      });

      const json = await res.json().catch(() => null);
      if (!res.ok || !json || json.ok !== true) {
        const msg = json && json.error ? json.error : `Request failed (${res.status})`;
        throw new Error(msg);
      }

      return json;
    } finally {
      clearTimeout(t);
    }
  }

  async function sendPdfNotification({ email, cart }) {
    const controller = new AbortController();
    const t = setTimeout(() => controller.abort(), 12000);

    try {
      const resumeUrl = buildCartPermalink(cart);
      const cartNumber = state.cartNumber || getCartNumberFromCart(cart) || getCartNumberFromLocal() || "";

      const payload = {
        action: "download_pdf",
        email,
        to: email,
        cart,
        resumeCartUrl: resumeUrl,
        cartNumber,
        cart_number: cartNumber
      };

      await fetch(EMAIL_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
        signal: controller.signal
      });
    } catch (e) {
      console.warn("[AGOZ_CART_DRAWER] PDF notification warning:", e);
    } finally {
      clearTimeout(t);
    }
  }

  function wireEmailView(bodyEl) {
    const cart = state.cart || { items: [], total_price: 0, attributes: {} };

    const nameEl = bodyEl.querySelector("[data-email-name]");
    const toEl = bodyEl.querySelector("[data-email-to]");
    const notesEl = bodyEl.querySelector("[data-email-notes]");

    bodyEl.querySelector("[data-email-cancel]")?.addEventListener("click", () => {
      if (state.email.sending) return;
      state.view = "cart";
      state.email.error = "";
      state.email.success = "";
      render();
    });

    bodyEl.querySelector("[data-email-send]")?.addEventListener("click", async () => {
      const name = (nameEl?.value || "").trim();
      const to = (toEl?.value || "").trim();
      const notes = (notesEl?.value || "").trim();

      state.email.error = "";
      state.email.success = "";

      if (!name) {
        state.email.error = "Please enter your full name.";
        render();
        return;
      }
      if (!to) {
        state.email.error = "Please enter your email.";
        render();
        return;
      }
      if (!isValidEmail(to)) {
        state.email.error = "Please enter a valid email.";
        render();
        return;
      }

      state.email.sending = true;
      render();

      try {
        await ensureCartNumber(cart);

        // ✅ Send email (No draft order created)
        await sendCartEmail({ to, name, notes, cart: state.cart || cart });

        state.email.success = "Email sent. Check your inbox.";
        state.email.error = "";
        state.email.sending = false;
        render();
      } catch (e) {
        state.email.error = e?.message || "Could not send email. Please try again.";
        state.email.success = "";
        state.email.sending = false;
        render();
      }
    });
  }

  function wireCart(bodyEl, footerEl, cart) {
    bodyEl.querySelectorAll("[data-line]").forEach((row) => {
      const line = Number(row.getAttribute("data-line"));
      const input = row.querySelector("[data-qty-input]");
      const minus = row.querySelector("[data-qty-minus]");
      const plus = row.querySelector("[data-qty-plus]");
      const remove = row.querySelector("[data-remove]");

      const commit = async (qty) => {
        qty = Math.max(0, parseInt(qty, 10) || 0);
        await changeLine(line, qty);
      };

      minus?.addEventListener("click", () => commit((parseInt(input.value, 10) || 0) - 1));
      plus?.addEventListener("click", () => commit((parseInt(input.value, 10) || 0) + 1));
      remove?.addEventListener("click", () => commit(0));

      input?.addEventListener("focus", () => input.select());
      input?.addEventListener("keydown", (e) => {
        if (e.key === "Enter") {
          e.preventDefault();
          input.blur();
        }
      });
      input?.addEventListener("blur", () => commit(input.value));
    });

    footerEl.querySelector("[data-checkout]")?.addEventListener("click", () => {
      window.location.href = "/checkout";
    });

    footerEl.querySelector("[data-email-cart]")?.addEventListener("click", () => {
      state.view = "email";
      state.email.error = "";
      state.email.success = "";
      render();
    });

    const pdfBtn = footerEl.querySelector("[data-download-pdf]");
    pdfBtn?.addEventListener("click", async () => {
      if (pdfBtn.disabled) return;
      const originalHtml = pdfBtn.innerHTML;

      try {
        pdfBtn.disabled = true;
        pdfBtn.classList.add("agoz-btn-loading");
        pdfBtn.innerHTML = `<span class="agoz-spinner"></span><span>Downloading...</span><div class="agoz-loading-bar"></div>`;

        await ensureCartNumber(cart);

        const gateEmail = (localStorage.getItem(LS_EMAIL) || state.gate.email || "").trim().toLowerCase();
        if (!gateEmail || !isValidEmail(gateEmail)) {
          alert("Please unlock your cart with a valid email first.");
          return;
        }

        // Send CS notification about PDF download (No draft order created)
        sendPdfNotification({ email: gateEmail, cart: state.cart || cart });

        await downloadPdf(state.cart || cart);

        // Dynamically snap progress bar to 100% completion when finished
        const bar = pdfBtn.querySelector(".agoz-loading-bar");
        if (bar) {
          bar.style.animation = "none";
          bar.style.transition = "width 0.2s ease-out";
          bar.style.width = "100%";
          await new Promise((r) => setTimeout(r, 220));
        }
      } catch (e) {
        alert("Could not download PDF: " + (e?.message || "Unknown error"));
      } finally {
        pdfBtn.disabled = false;
        pdfBtn.classList.remove("agoz-btn-loading");
        pdfBtn.innerHTML = originalHtml;
      }
    });
  }

  async function changeLine(line, quantity) {
    try {
      const res = await fetch("/cart/change.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "same-origin",
        body: JSON.stringify({ line, quantity })
      });
      if (!res.ok) throw new Error("change.js failed");
      state.cart = await res.json();

      state.cartNumber = getCartNumberFromCart(state.cart) || state.cartNumber || getCartNumberFromLocal();

      render();
    } catch (e) {
      console.warn("[AGOZ_CART_DRAWER] changeLine failed", e);
      alert("Could not update cart. Please try again.");
      refresh();
    }
  }

  // ---- PDF ----
  function loadScriptOnce(id, src) {
    return new Promise((resolve, reject) => {
      if (document.getElementById(id)) return resolve();
      const s = document.createElement("script");
      s.id = id;
      s.src = src;
      s.async = true;
      s.onload = () => resolve();
      s.onerror = () => reject(new Error("Failed to load: " + src));
      document.head.appendChild(s);
    });
  }

  async function ensureJsPDF() {
    if (window.jspdf && window.jspdf.jsPDF) return;

    await loadScriptOnce(
      "agoz-jspdf",
      "https://cdn.jsdelivr.net/npm/jspdf@2.5.1/dist/jspdf.umd.min.js"
    );

    if (!window.jspdf || !window.jspdf.jsPDF) {
      throw new Error("jsPDF is not loaded on this page (window.jspdf.jsPDF missing).");
    }
  }

  async function ensureAutoTable() {
    const { jsPDF } = window.jspdf;
    const testDoc = new jsPDF({ unit: "pt", format: "letter" });
    if (typeof testDoc.autoTable === "function") return;

    await loadScriptOnce(
      "agoz-autotable",
      "https://cdn.jsdelivr.net/npm/jspdf-autotable@3.8.3/dist/jspdf.plugin.autotable.min.js"
    );

    const testDoc2 = new jsPDF({ unit: "pt", format: "letter" });
    if (typeof testDoc2.autoTable !== "function") {
      throw new Error("AutoTable failed to attach to jsPDF.");
    }
  }

  async function fetchImageAsDataURL(url) {
    try {
      const res = await fetch(url, { mode: "cors", cache: "no-store" });
      if (!res.ok) throw new Error("Logo fetch failed");
      const blob = await res.blob();

      return await new Promise((resolve, reject) => {
        const r = new FileReader();
        r.onload = () => resolve(String(r.result || ""));
        r.onerror = reject;
        r.readAsDataURL(blob);
      });
    } catch (e) {
      return "";
    }
  }

  function detectImageTypeFromDataUrl(dataUrl) {
    const s = String(dataUrl || "");
    if (s.startsWith("data:image/png")) return "PNG";
    if (s.startsWith("data:image/jpeg") || s.startsWith("data:image/jpg")) return "JPEG";
    return "PNG";
  }

  function getImageDimensions(dataUrl) {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () =>
        resolve({ w: img.naturalWidth || img.width, h: img.naturalHeight || img.height });
      img.onerror = () => resolve({ w: 0, h: 0 });
      img.src = dataUrl;
    });
  }

  function moneySafe(cents) {
    if (typeof window.money === "function") return window.money(cents);
    if (typeof money === "function") return money(cents);

    const n = Number(cents || 0);
    return `$${(n / 100).toFixed(2)}`;
  }

  async function downloadPdf(cart) {
    try {
      await ensureJsPDF();
      await ensureAutoTable();

      const cartNumber = state.cartNumber || getCartNumberFromCart(cart) || getCartNumberFromLocal() || "";

      const { jsPDF } = window.jspdf;
      const doc = new jsPDF({ unit: "pt", format: "letter" });

      const pageW = doc.internal.pageSize.getWidth();
      const pageH = doc.internal.pageSize.getHeight();
      const margin = 52;

      const COL_DARK = [17, 24, 39];
      const COL_MUTED = [107, 114, 128];
      const COL_LINE = [229, 231, 235];
      const COL_HEAD_BG = [248, 250, 252];

      const LOGO_URL =
        "https://cdn.shopify.com/s/files/1/0010/6633/6311/files/lastlogo.png?v=1767971252";

      const logoDataUrl = await fetchImageAsDataURL(LOGO_URL);
      const logoType = detectImageTypeFromDataUrl(logoDataUrl);
      const logoDims = logoDataUrl ? await getImageDimensions(logoDataUrl) : { w: 0, h: 0 };

      const headerTopY = 46;
      const logoH = 28;
      const logoW =
        logoDims.w && logoDims.h ? Math.round(logoH * (logoDims.w / logoDims.h)) : 150;

      const docTitle = "CART";
      const docDate = new Date().toLocaleDateString();

      function drawHeader() {
        if (logoDataUrl) {
          doc.addImage(logoDataUrl, logoType, margin, headerTopY - logoH + 6, logoW, logoH);
        } else {
          doc.setFont("helvetica", "bold");
          doc.setFontSize(18);
          doc.setTextColor(...COL_DARK);
          doc.text("AGOZTECH", margin, headerTopY);
        }

        doc.setTextColor(...COL_DARK);
        doc.setFont("helvetica", "bold");
        doc.setFontSize(22);
        doc.text(docTitle, pageW - margin, headerTopY, { align: "right" });

        doc.setTextColor(...COL_MUTED);
        doc.setFont("helvetica", "normal");
        doc.setFontSize(11);

        doc.text(`Date: ${docDate}`, pageW - margin, headerTopY + 18, { align: "right" });

        if (cartNumber) {
          doc.text(`Cart #: ${cartNumber}`, pageW - margin, headerTopY + 34, { align: "right" });
        }

        doc.setDrawColor(...COL_LINE);
        doc.setLineWidth(2);
        doc.line(margin, headerTopY + 44, pageW - margin, headerTopY + 44);
      }

      function drawFooter() {
        const footerLines = [
          "AgozTech LLC",
          "3010 Eaton Ave Ste B",
          "Indian Trail, NC 28079",
          "704-882-0133",
        ];

        const lineH = 13;
        const footerBottomPad = 18;
        const blockH = (footerLines.length - 1) * lineH;
        const startY = pageH - margin - footerBottomPad - blockH;

        doc.setDrawColor(...COL_LINE);
        doc.setLineWidth(2);
        doc.line(margin, startY - 16, pageW - margin, startY - 16);

        const centerX = pageW / 2;

        doc.setFont("helvetica", "normal");
        doc.setFontSize(11);
        doc.setTextColor(...COL_MUTED);

        footerLines.forEach((t, i) => {
          doc.text(t, centerX, startY + i * lineH, { align: "center" });
        });
      }

      drawHeader();

      const items = Array.isArray(cart?.items) ? cart.items : [];

      const body = items.length
        ? items.map((it) => {
            const title = String(it?.product_title || it?.title || "").trim();
            const sku = String(it?.sku || "").trim();
            const qty = Number(it?.quantity || 0);

            const unit = Number(it?.price || 0);
            const lineTotal = it?.line_price != null ? Number(it.line_price) : unit * qty;

            const desc = sku ? `${title}\nSKU: ${sku}` : title;

            return [desc, String(qty), moneySafe(unit), moneySafe(lineTotal)];
          })
        : [["(Cart is empty)", "", "", ""]];

      const tableW = pageW - margin * 2;
      const wDesc = Math.round(tableW * 0.58);
      const wQty = Math.round(tableW * 0.10);
      const wUnit = Math.round(tableW * 0.16);
      const wAmt = tableW - wDesc - wQty - wUnit;

      const tableStartY = headerTopY + 120;

      doc.autoTable({
        startY: tableStartY,
        head: [["DESCRIPTION", "QTY", "UNIT PRICE", "AMOUNT"]],
        body,
        theme: "plain",
        tableWidth: tableW,
        margin: { left: margin, right: margin },

        styles: {
          font: "helvetica",
          fontSize: 9,
          textColor: COL_DARK,
          cellPadding: { top: 14, bottom: 14, left: 10, right: 10 },
          valign: "top",
          overflow: "linebreak",
        },

        headStyles: {
          fillColor: COL_HEAD_BG,
          textColor: COL_MUTED,
          fontStyle: "bold",
          fontSize: 10,
          cellPadding: { top: 14, bottom: 14, left: 10, right: 10 },
        },

        columnStyles: {
          0: { cellWidth: wDesc, halign: "left" },
          1: { cellWidth: wQty, halign: "center" },
          2: { cellWidth: wUnit, halign: "right" },
          3: { cellWidth: wAmt, halign: "right" },
        },

        didDrawPage: () => {
          drawHeader();
          drawFooter();
        },

        didDrawCell: (data) => {
          if (data.section === "body" && data.column.index === 0) {
            const y = data.cell.y + data.cell.height;
            doc.setDrawColor(...COL_LINE);
            doc.setLineWidth(2);
            doc.line(margin, y, pageW - margin, y);
          }

          if (data.section === "head" && data.column.index === 0) {
            const y = data.cell.y + data.cell.height;
            doc.setDrawColor(...COL_LINE);
            doc.setLineWidth(2);
            doc.line(margin, y, pageW - margin, y);
          }
        },
      });

      const afterTableY = (doc.lastAutoTable?.finalY || tableStartY) + 26;

      const footerSafeTopY = pageH - margin - 120;
      let blockY = afterTableY;

      if (blockY > footerSafeTopY) {
        doc.addPage();
        blockY = headerTopY + 120;
      }

      const totalCents = cart?.total_price || 0;

      const blockW = 260;
      const blockX = pageW - margin - blockW;
      const rowH = 18;

      doc.setDrawColor(...COL_LINE);
      doc.setLineWidth(2);
      doc.line(blockX, blockY, blockX + blockW, blockY);

      const labelX = blockX;
      const valueX = blockX + blockW;

      function totalsRow(label, value, y, opts = {}) {
        doc.setFont("helvetica", opts.bold ? "bold" : "normal");
        doc.setFontSize(opts.size || 11);
        doc.setTextColor(...(opts.color || COL_MUTED));
        doc.text(label, labelX, y);

        doc.setFont("helvetica", opts.bold ? "bold" : "normal");
        doc.setFontSize(opts.size || 11);
        doc.setTextColor(...(opts.valueColor || COL_DARK));
        doc.text(value, valueX, y, { align: "right" });
      }

      let y = blockY + 18;
      totalsRow("Subtotal", moneySafe(totalCents), y);
      y += rowH;

      totalsRow("Shipping", "TBD", y);
      y += rowH;

      totalsRow("Taxes", "TBD", y);
      y += rowH;

      doc.setDrawColor(...COL_LINE);
      doc.setLineWidth(2);
      doc.line(blockX, y - 8, blockX + blockW, y - 8);

      totalsRow("Total", moneySafe(totalCents), y + 6, {
        bold: true,
        size: 12,
        color: COL_DARK,
        valueColor: COL_DARK,
      });

      const filename = `AgozTech-Cart-${cartNumber || "XXXXXX"}-${new Date().toISOString().slice(0, 10)}.pdf`;
      doc.save(filename);
    } catch (e) {
      console.warn("[AGOZ_CART_DRAWER] PDF failed", e);
      alert("PDF download failed: " + (e?.message || "Unknown error"));
    }
  }

  function preloadPdfLibs() {
    return;
  }

  // --------------------------
  // Icons
  // --------------------------
  function iconClose() {
    return `<svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M6 6L18 18M18 6L6 18" stroke="#111827" stroke-width="2.5" stroke-linecap="round"/>
    </svg>`;
  }

  function iconBack() {
    return `<svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M15 18l-6-6 6-6" stroke="#111827" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
    </svg>`;
  }

  function iconCart() {
    return `<svg width="26" height="26" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M6 6h15l-2 9H7L6 6Z" stroke="#111827" stroke-width="2" stroke-linejoin="round"/>
      <path d="M6 6l-1-3H2" stroke="#111827" stroke-width="2" stroke-linecap="round"/>
      <path d="M9 20a1 1 0 1 0 0-2 1 1 0 0 0 0 2ZM18 20a1 1 0 1 0 0-2 1 1 0 0 0 0 2Z" fill="#111827"/>
    </svg>`;
  }

  window.AGOZ_CART_DRAWER = { open, close, toggle, refresh };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", mount);
  } else {
    mount();
  }
})();
